import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaTachometerAlt, 
  FaCog, 
  FaChartBar, 
  FaTimes,
  FaUserShield,
  FaQuestionCircle,
  FaEnvelope,
  FaDatabase,
  FaVideo,
  FaBoxOpen,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCreditCard,
  FaDumbbell,
  FaUserFriends,
  FaBullseye,
  FaBrain,
  FaCalendarAlt,
  FaProjectDiagram,
  FaNewspaper,
  FaPaperPlane,
  FaPalette,
  FaUsers,
  FaListUl,
  FaChartLine
} from 'react-icons/fa';

import { useApp } from '../../contexts/AppContext';
import { useTranslation } from '../../hooks/useTranslation';
import config from '../../config/app.config';

export const isSidebarRoute = (pathname) => {
  return pathname.startsWith('/admin') || 
         pathname.startsWith('/dashboard') || 
         pathname.startsWith('/monitor-protected');
};

const SidebarWrapper = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${props => props.$collapsed ? '80px' : '300px'};
  background: ${props => props.theme === 'dark' 
    ? 'rgba(15, 23, 42, 0.95)' 
    : 'rgba(0, 0, 0, 0.95)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1100;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (min-width: 1024px) {
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
  
  @media (max-width: 1023px) {
    width: 320px;
    max-width: 85vw;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(to bottom, transparent, #e63946, transparent);
    opacity: 0.5;
  }
`;

const SidebarContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  
  @media (max-width: 1023px) {
    background: ${props => props.theme === 'dark' 
      ? 'rgba(15, 23, 42, 0.98)' 
      : 'rgba(0, 0, 0, 0.98)'};
    backdrop-filter: blur(20px);
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 60%;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e63946, transparent);
    transform: translateX(-50%);
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 12px;
    background: linear-gradient(45deg, transparent, rgba(230, 57, 70, 0.2), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Logo = styled.img`
  height: 50px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: scale(1.08) rotate(2deg);
  }
`;

const AppName = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  display: ${props => props.$collapsed ? 'none' : 'block'};
  transition: all 0.3s ease;
  
  @media (max-width: 1023px) {
    display: block !important;
  }
`;

const CollapseButton = styled.button`
  position: absolute;
  top: 50%;
  right: -15px;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e63946;
  border: 2px solid ${props => props.theme === 'dark' 
    ? 'rgba(15, 23, 42, 1)' 
    : 'rgba(255, 255, 255, 1)'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1101;
  
  &:hover {
    background: #d52c3d;
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 1023px) {
    display: none;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
    transform: rotate(90deg);
  }
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: 1.5rem 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #e63946 transparent;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #e63946;
    border-radius: 2px;
  }
`;

const NavSection = styled.div`
  margin-bottom: 2.5rem;
  
  &:last-child {
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${props => props.theme === 'dark' ? '#94a3b8' : '#6b7280'};
  margin: 0 1.5rem 1rem;
  display: ${props => props.$collapsed ? 'none' : 'block'};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(90deg, #e63946, transparent);
    border-radius: 1px;
  }
  
  @media (max-width: 1023px) {
    display: block !important;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0'};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 1rem;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(230, 57, 70, 0.1), rgba(230, 57, 70, 0.05));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    color: #e63946;
    transform: translateX(5px);
    
    &::before {
      opacity: 1;
    }
    
    .nav-icon {
      transform: scale(1.2);
      color: #e63946;
    }
  }
  
  &.active {
    color: #e63946;
    background: linear-gradient(135deg, rgba(230, 57, 70, 0.15), rgba(230, 57, 70, 0.05));
    border-left: 3px solid #e63946;
    
    &::before {
      opacity: 1;
    }
    
    .nav-icon {
      color: #e63946;
      transform: scale(1.1);
    }
    
    .nav-label {
      font-weight: 600;
    }
  }
  
  .nav-icon {
    flex-shrink: 0;
    color: ${props => props.theme === 'dark' ? '#94a3b8' : '#6b7280'};
    transition: all 0.3s ease;
    font-size: 1.1rem;
  }
  
  .nav-label {
    display: ${props => props.$collapsed ? 'none' : 'block'};
    transition: all 0.3s ease;
    
    @media (max-width: 1023px) {
      display: block !important;
    }
  }
  
  .nav-badge {
    margin-left: auto;
    background: #e63946;
    color: white;
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
    border-radius: 10px;
    display: ${props => props.$collapsed ? 'none' : 'block'};
    
    @media (max-width: 1023px) {
      display: block !important;
    }
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0'};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 1rem;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  width: calc(100% - 2rem);
  text-align: left;
  
  &:hover {
    color: #e63946;
    transform: translateX(5px);
    
    .nav-icon {
      transform: scale(1.2);
      color: #e63946;
    }
  }
  
  .nav-icon {
    flex-shrink: 0;
    color: ${props => props.theme === 'dark' ? '#94a3b8' : '#6b7280'};
    transition: all 0.3s ease;
    font-size: 1.1rem;
  }
  
  .nav-label {
    display: ${props => props.$collapsed ? 'none' : 'block'};
    transition: all 0.3s ease;
    
    @media (max-width: 1023px) {
      display: block !important;
    }
  }
`;

const SidebarFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 60%;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e63946, transparent);
    transform: translateX(-50%);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  padding: 1rem;
  border-radius: 12px;
  background: ${props => props.theme === 'dark' 
    ? 'rgba(51, 65, 85, 0.8)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.2)'};
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: ${props => props.theme === 'dark' 
      ? 'rgba(71, 85, 105, 0.9)' 
      : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-1px);
  }
  
  .avatar {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e63946 0%, #d52c3d 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 1rem;
    box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
    border: 2px solid rgba(255, 255, 255, 0.2);
  }
  
  .user-details {
    flex: 1;
    min-width: 0;
    display: ${props => props.$collapsed ? 'none' : 'block'};
    
    @media (max-width: 1023px) {
      display: block !important;
    }
    
    .name {
      font-weight: 600;
      font-size: 0.9rem;
      color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0.25rem;
    }
    
    .role {
      font-size: 0.75rem;
      color: #e63946;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1050;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const Sidebar = () => {
  const { auth, theme, sidebarOpen, toggleSidebar, sidebarCollapsed, toggleSidebarCollapse } = useApp();
  const { tNav, tAdmin, tSidebar, tAuth, tCommon } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const shouldShowSidebar = () => {
    if (!auth.authenticated) return false;
    return isSidebarRoute(location.pathname);
  };

  if (!shouldShowSidebar()) {
    return null;
  }

  const isActive = (path) => {
    if (path === '/admin' || path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate('/');
    } catch (error) {
      console.error('Error en logout:', error);
      navigate('/login');
    }
  };

  const getNavigationItems = () => {
    const sections = [
      {
        section: tNav('dashboard'),
        items: [
          { path: '/dashboard', label: tNav('dashboard'), icon: FaTachometerAlt },
          { path: '/monitor-protected', label: tNav('monitor'), icon: FaChartBar },
        ]
      }
    ];

    const isAdmin = auth.isAdmin() || 
                   auth.hasRole('admin') || 
                   auth.hasRole('administrator') ||
                   auth.hasRole('default-roles-punchia');

    if (isAdmin) {
      sections.push({
        section: tAdmin('title'),
        items: [
          { path: '/admin', label: tAdmin('title'), icon: FaUserShield, badge: 'Admin' },
          { path: '/admin/users', label: 'Usuarios', icon: FaUsers },
          { path: '/admin/contacts', label: tAdmin('contacts.title'), icon: FaEnvelope },
          { path: '/admin/data', label: tAdmin('data.title'), icon: FaDatabase },
          { path: '/admin/videos', label: tAdmin('videos.title', { defaultValue: 'Videos' }), icon: FaVideo },
          { path: '/admin/combos', label: tAdmin('combos.title', { defaultValue: 'Combos' }), icon: FaBoxOpen },
          { path: '/admin/subscriptions', label: tAdmin('subscriptions.title', { defaultValue: 'Suscripciones' }), icon: FaCreditCard },
          { path: '/admin/training-sessions', label: tAdmin('trainingSessions.title', { defaultValue: 'Training Sessions' }), icon: FaDumbbell },
          { path: '/admin/coach-links', label: tAdmin('coachLinks.title', { defaultValue: 'Coach Links' }), icon: FaUserFriends },
          { path: '/admin/goals', label: tAdmin('goals.title', { defaultValue: 'Goals' }), icon: FaBullseye },
          { path: '/admin/plan-builder', label: 'Plan Builder', icon: FaProjectDiagram, badge: 'Pro' },
          { path: '/admin/adaptive-plans', label: tAdmin('adaptivePlans.title', { defaultValue: 'Adaptive Plans' }), icon: FaBrain },
          { path: '/admin/programs', label: tAdmin('programs.title', { defaultValue: 'Programas 12 semanas' }), icon: FaCalendarAlt },
          { path: '/admin/config', label: tAdmin('config.title'), icon: FaCog }
        ]
      });

      sections.push({
        section: 'Email Marketing',
        items: [
          { path: '/admin/email', label: 'Campañas', icon: FaPaperPlane },
          { path: '/admin/email/templates', label: 'Templates', icon: FaPalette },
        ]
      });

      sections.push({
        section: 'Newsletter',
        items: [
          { path: '/admin/newsletter', label: 'Ediciones', icon: FaNewspaper },
          { path: '/admin/newsletter/subscribers', label: 'Suscriptores', icon: FaUsers },
          { path: '/admin/newsletter/lists', label: 'Listas', icon: FaListUl },
          { path: '/admin/newsletter/stats', label: 'Estadísticas', icon: FaChartLine },
        ]
      });
    }

    return sections;
  };

  const navigationSections = getNavigationItems();

  return (
    <>
      <Overlay $isOpen={sidebarOpen} onClick={toggleSidebar} />
      
      <SidebarWrapper $isOpen={sidebarOpen} $collapsed={sidebarCollapsed} theme={theme}>
        <SidebarContent theme={theme}>
          <SidebarHeader>
            {sidebarCollapsed ? (
              <LogoContainer to="/">
                <Logo src="/logo192.png" alt={`${config.app.name} Logo`} />
                <AppName theme={theme} $collapsed={sidebarCollapsed}>
                  {config.app.name}
                </AppName>
              </LogoContainer>
            ) : (
              <LogoContainer to="/">
                <Logo src="/Logo.jpg" alt={`${config.app.name} Logo`} />
                <AppName theme={theme} $collapsed={sidebarCollapsed}>
                  {config.app.name}
                </AppName>
              </LogoContainer>
            )}
            
            <CollapseButton 
              onClick={toggleSidebarCollapse}
              theme={theme}
              title={sidebarCollapsed ? tSidebar('expand') : tSidebar('collapse')}
            >
              {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </CollapseButton>
            
            <CloseButton onClick={toggleSidebar} theme={theme}>
              <FaTimes />
            </CloseButton>
          </SidebarHeader>

          <SidebarNav>
            {navigationSections.map((section, sectionIndex) => (
              <NavSection key={sectionIndex}>
                <SectionTitle theme={theme} $collapsed={sidebarCollapsed}>
                  {section.section}
                </SectionTitle>
                {section.items.map((item, itemIndex) => (
                  item.external ? (
                    <NavItem
                      key={itemIndex}
                      as="a"
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      $collapsed={sidebarCollapsed}
                      theme={theme}
                    >
                      <item.icon className="nav-icon" />
                      <span className="nav-label">{item.label}</span>
                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                    </NavItem>
                  ) : (
                    <NavItem
                      key={itemIndex}
                      to={item.path}
                      className={isActive(item.path) ? 'active' : ''}
                      onClick={handleNavClick}
                      $collapsed={sidebarCollapsed}
                      theme={theme}
                    >
                      <item.icon className="nav-icon" />
                      <span className="nav-label">{item.label}</span>
                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                    </NavItem>
                  )
                ))}
              </NavSection>
            ))}

            <NavSection>
              <SectionTitle theme={theme} $collapsed={sidebarCollapsed}>
                {tNav('help')}
              </SectionTitle>
              
              <NavItem
                to="/contact"
                className={isActive('/contact') ? 'active' : ''}
                onClick={handleNavClick}
                $collapsed={sidebarCollapsed}
                theme={theme}
              >
                <FaQuestionCircle className="nav-icon" />
                <span className="nav-label">{tNav('support')}</span>
              </NavItem>
              
              <ActionButton
                onClick={handleLogout}
                $collapsed={sidebarCollapsed}
                theme={theme}
              >
                <FaSignOutAlt className="nav-icon" />
                <span className="nav-label">{tAuth('logout')}</span>
              </ActionButton>
            </NavSection>
          </SidebarNav>

          {auth.authenticated && auth.user && (
            <SidebarFooter>
              <UserInfo theme={theme} $collapsed={sidebarCollapsed}>
                <div className="avatar">
                  {(auth.user.firstName || auth.user.name || tCommon('user')).charAt(0).toUpperCase()}
                </div>
                <div className="user-details">
                  <div className="name">
                    {auth.user.firstName || auth.user.name || tCommon('user')}
                  </div>
                  <div className="role">
                    {auth.isAdmin() || auth.hasRole('admin') || auth.hasRole('default-roles-punchia') 
                      ? 'Administrador' 
                      : 'Usuario'}
                  </div>
                </div>
              </UserInfo>
            </SidebarFooter>
          )}
        </SidebarContent>
      </SidebarWrapper>
    </>
  );
};

export default Sidebar;
